import { useCallback, useEffect, useRef } from "react";
import ReactMapGL, {
  Layer,
  MapLayerMouseEvent,
  Source,
  ViewStateChangeEvent,
} from "react-map-gl";
import styles from "./CybersecurityMap.module.css";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import { useActions } from "../../../../hooks/useActions";
import { useLazyEffect } from "../../../../hooks/useLazyEffect";

import CustomNavigationControl from "../../_mapComponents/CustomNavigationControl/CustomNavigationControl";
import CustomMarker from "../../_mapComponents/CustomMarker/CustomMarker";
import { mapboxAccessToken } from "../../../../constants/mapConstants";
import useSearchParams from "../../../../hooks/useSearchParams";
import useSelectedLanguage from "../../../../hooks/useSelectedLanguage";
import { changeMapboxLanguage } from "../../../../util/mapInterfaceUtil";
import useCybersecurityFilters from "./useCybersecurityFilters";
import MapWrapper from "../../../../share/mapComponents/MapWrapper";
import MapBadgeLoader from "../../_mapComponents/MapBadgeLoader";
import useMapObjects from "../../../../hooks/useMapObjects";
import PinnedSelectedMapObjectsList from "../../_mapComponents/PinnedSelectedMapObjectsList";
import CustomPopup from "../../_mapComponents/CustomPopup";
import CustomMapPopupItem from "../../_mapComponents/CustomPopup/CustomMapPopupItem";
import useMobileScreen from "../../../../hooks/useMobileScreen";
import { languages } from "../../../../constants/constants";
import {
  getCybersecurityIconForPoint,
  getDomainFromAddress,
} from "../../../../util/cyberSecurityUtil";
import { CyberSecurityFeature } from "../../../../types/CyberSecurityTypes";
import util, { getWebsiteFavicon } from "../../../../util";

const CYBERSECURITY_LAYER_ID = "cybersecurity-points";

const FALLBACK_LANGUAGE = languages.en;

const cybersecurityConfig = {
  [languages.ua]: {
    mapStyle: "mapbox://styles/aimap/cmfax5v60000101r10dwf4y14",
    source: "mapbox://aimap.7odn1xik",
    sourceLayer: "cybersecurity_cluster-2jnkxs",
  },
  [languages.en]: {
    mapStyle: "mapbox://styles/aimap/cmfaxcqy6003t01sd379j7qv3",
    source: "mapbox://aimap.6eqydmcx",
    sourceLayer: "cybersecurity_cluster_en-bz9dvs",
  },
} as const;

const getCybersecurityConfig = (language: string) =>
  cybersecurityConfig[language] ?? cybersecurityConfig[FALLBACK_LANGUAGE];

const mainServiceCategoryColorConfig: Array<{
  values: string[];
  color: string;
}> = [
  { values: ["MSSP"], color: "#8E44AD" },
  {
    values: ["Audit / Consulting"],
    color: "#95A5A6",
  },
  {
    values: ["Integration", "Integrator", "Інтеграція", "Інтегратор"],
    color: "#007BFF",
  },
  { values: ["Ethical Hacking", "Етичний хакінг"], color: "#27AE60" },
  { values: ["Vendor", "Вендор"], color: "#E74C3C" },
  { values: ["Training", "Навчання"], color: "#F1C40F" },
  { values: ["Distributor", "Дистриб'ютор"], color: "#E67E22" },
  { values: ["Other", "Інше"], color: "#2ECC71" },
];

const getCategoryVariants = (value: string): string[] => {
  const trimmed = value.trim();

  if (!trimmed) {
    return [];
  }

  const lower = value.toLowerCase();
  const lowerTrimmed = trimmed.toLowerCase();

  const variants = new Set<string>([value, trimmed, lower, lowerTrimmed]);

  const variantsWithSpaces = [
    ` ${trimmed}`,
    `${trimmed} `,
    ` ${trimmed} `,
    ` ${lowerTrimmed}`,
    `${lowerTrimmed} `,
    ` ${lowerTrimmed} `,
  ];

  variantsWithSpaces.forEach((variant) => {
    if (variant.trim()) {
      variants.add(variant);
    }
  });

  return Array.from(variants);
};

const mainServiceCategoryExpressionValues =
  mainServiceCategoryColorConfig.reduce<string[]>((acc, { values, color }) => {
    const categoryVariants = new Set<string>();

    values.forEach((value) => {
      getCategoryVariants(value).forEach((variant) => {
        categoryVariants.add(variant);
      });
    });

    categoryVariants.forEach((variant) => {
      acc.push(variant, color);
    });

    return acc;
  }, []);

const cybersecurityCircleLayerPaint: any = {
  "circle-radius": 8,
  "circle-color": [
    "match",
    ["coalesce", ["get", "main_service_category"], ""],
    ...mainServiceCategoryExpressionValues,
    "#AAA",
  ],
  "circle-stroke-width": 2,
  "circle-stroke-color": "#FFFFFF",
};

const CybersecurityMap = () => {
  const {
    isFilterBarActive,
    isListViewActive,
    viewport,
    currentMarker,
    offset,
    isDetailsActive,
  } = useTypedSelector((state) => state.mapInterface);
  const {
    activeSuggestions,
    services,
    service_category,
    company_size,
    registration_date__gte,
    registration_date__lte,
    company_type,
    other_services,
    certifications,
  } = useTypedSelector((state) => state.cybersecurityMapFilters);
  const { keyword } = useTypedSelector((state) => state.mapFilters);
  const selectedObject = useTypedSelector(
    (state) => state.mapCybersecurityInfo.selectedObject
  );
  const { cybersecurityMapRef } = useTypedSelector(
    (state) => state.mapObjectsInfo
  );

  const {
    setViewport,
    getCybersecurityList,
    setCurrentMapRef,
    setIsDetailsActive,
    setCurrentMarker,
    setIsFilterActive,
  } = useActions();

  const previousViewportRef = useRef(viewport);
  const pendingViewportUpdateRef = useRef(false);
  const lastInteractionRef = useRef(false);

  const hasViewportChanged = (
    prev: typeof viewport,
    next: typeof viewport
  ): boolean => {
    if (!prev || !next) {
      return false;
    }

    return (
      prev.latitude !== next.latitude ||
      prev.longitude !== next.longitude ||
      prev.zoom !== next.zoom ||
      prev.bearing !== next.bearing ||
      prev.pitch !== next.pitch
    );
  };
  const mapFilter = useCybersecurityFilters();
  const {
    currObjs,
    pinnedCurrObjs,
    setPinnedCurrObjs,
    markerClickHandler,
    markerHoverHandler,
    handlePopupMouseEnter,
    handlePopupMouseLeave,
  } = useMapObjects();
  const typedCurrObjects = currObjs as CyberSecurityFeature[];
  useEffect(() => {
    setCurrentMapRef(cybersecurityMapRef);
  }, [cybersecurityMapRef]);

  useLazyEffect(() => {
    getCybersecurityList();
    // eslint-disable-next-line
  }, [
    offset,
    // activeSuggestions,
    services,
    company_size,
    service_category,
    registration_date__gte,
    registration_date__lte,
    company_type,
    services,
    other_services,
    certifications,
    keyword,
  ]);

  useEffect(() => {
    getCybersecurityList();
  }, [activeSuggestions, keyword]);

  useLazyEffect(
    () => {
      const previousViewport = previousViewportRef.current;
      const viewportChanged = hasViewportChanged(previousViewport, viewport);
      const wasUserInteraction = lastInteractionRef.current;

      if (viewportChanged) {
        previousViewportRef.current = viewport;
      }

      lastInteractionRef.current = false;

      if (isDetailsActive) {
        if (viewportChanged && wasUserInteraction) {
          pendingViewportUpdateRef.current = true;
        }
        return;
      }

      if (
        (viewportChanged && wasUserInteraction) ||
        pendingViewportUpdateRef.current
      ) {
        pendingViewportUpdateRef.current = false;
        getCybersecurityList();
      }
    },
    [viewport, isDetailsActive],
    700
  );

  useEffect(() => {
    if (selectedObject?.location?.lat && selectedObject?.location?.lon) {
      setIsDetailsActive(true);
      setIsFilterActive(true);
      cybersecurityMapRef.current?.flyTo({
        center: [+selectedObject.location.lat, +selectedObject.location.lon],
        zoom: 10,
      });
      setCurrentMarker({
        lat: +selectedObject.location.lon,
        lon: +selectedObject.location.lat,
      });
    }
  }, [selectedObject]);

  useEffect(() => {
    if (!isListViewActive && cybersecurityMapRef.current) {
      cybersecurityMapRef.current.resize();
    }
  }, [isFilterBarActive, isListViewActive]);

  const { setSearchParam } = useSearchParams();
  const selectedLanguage = useSelectedLanguage();
  const { mapStyle, source, sourceLayer } =
    getCybersecurityConfig(selectedLanguage);

  const handleMapLoad = useCallback(() => {
    const map = cybersecurityMapRef.current?.getMap();

    if (map) {
      changeMapboxLanguage(map, selectedLanguage);
      util.mapInterfaceUtil.addPinIconToMapbox(map);
    }
  }, [selectedLanguage]);

  const getFeatures = (e: MapLayerMouseEvent) => {
    if (!e.point || !cybersecurityMapRef.current) return [];

    const { x, y } = e.point;
    const padding = 12;
    const queryBounds: [[number, number], [number, number]] = [
      [Math.max(x - padding, 0), Math.max(y - padding, 0)],
      [x + padding, y + padding],
    ];

    return cybersecurityMapRef.current.queryRenderedFeatures(queryBounds, {
      layers: [CYBERSECURITY_LAYER_ID],
      filter: mapFilter,
      validate: false,
    });
  };

  const renderPopupItem = (currObj: CyberSecurityFeature, index: number) => {
    const websiteRaw = currObj.properties.website?.trim();
    const websiteHref =
      websiteRaw && websiteRaw.length > 0
        ? websiteRaw.startsWith("http")
          ? websiteRaw
          : `https://${websiteRaw}`
        : undefined;

    const edrpouCode = currObj.properties.edrpou;

    const faviconSrc = getWebsiteFavicon(currObj?.properties.website);

    const logoSrc =
      faviconSrc ??
      (currObj ? getCybersecurityIconForPoint(currObj) : undefined);

    return (
      <CustomMapPopupItem
        isSelected={
          selectedObject &&
          currObj.properties.id === String(selectedObject?.edrpou)
        }
        key={index}
        onClick={() => {
          setSearchParam("hash_id", currObj.properties.edrpou);
        }}
      >
        <div>
          <img src={logoSrc} alt="icon" width={40} height={40} />
          <h4 className={styles.titleStyle}>
            {currObj.properties.company_name}
          </h4>
          {currObj.properties.main_service_category && (
            <p className={styles.primaryType}>
              {currObj.properties.main_service_category === "Навчання"
                ? "Курси"
                : currObj.properties.main_service_category}
            </p>
          )}
          <p className={styles.meta}>
            <span>{edrpouCode}</span>
            {websiteHref && websiteRaw && (
              <>
                <span>&middot;</span>
                <a
                  href={websiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.metaLink}
                  onClick={(event) => event.stopPropagation()}
                >
                  {getDomainFromAddress(websiteRaw)}
                </a>
              </>
            )}
          </p>
        </div>
      </CustomMapPopupItem>
    );
  };

  const isMobile = useMobileScreen();

  const handleViewportChange = (event: ViewStateChangeEvent) => {
    const interactionState = event.interactionState || {};
    const isUserInteraction = Boolean(
      interactionState.isDragging ||
        interactionState.isPanning ||
        interactionState.isZooming ||
        interactionState.isRotating
    );

    lastInteractionRef.current = isUserInteraction;
    setViewport(event.viewState);
  };

  return (
    <MapWrapper>
      {pinnedCurrObjs?.length > 0 && !selectedObject?.edrpou && isMobile && (
        <PinnedSelectedMapObjectsList handleClose={() => setPinnedCurrObjs([])}>
          {pinnedCurrObjs.map((currObj, index) =>
            renderPopupItem(currObj, index)
          )}
        </PinnedSelectedMapObjectsList>
      )}
      <ReactMapGL
        {...viewport}
        mapboxAccessToken={mapboxAccessToken}
        ref={(instance) => (cybersecurityMapRef.current = instance)}
        minZoom={5}
        maxZoom={22}
        maxPitch={2}
        dragRotate={false}
        mapStyle={mapStyle}
        onLoad={handleMapLoad}
        onMove={handleViewportChange}
        onZoom={handleViewportChange}
        onMouseOver={(e) => markerHoverHandler(getFeatures(e))}
        onClick={(e) => markerClickHandler(getFeatures(e), "edrpou")}
        onMouseMove={(e) => markerHoverHandler(getFeatures(e))}
        onMouseLeave={() => markerHoverHandler([])}
      >
        <CustomNavigationControl />
        <MapBadgeLoader />

        <Source type="vector" url={source}>
          <Layer
            filter={mapFilter}
            id={CYBERSECURITY_LAYER_ID}
            source-layer={sourceLayer}
            minzoom={5}
            maxzoom={22}
            type="circle"
            paint={cybersecurityCircleLayerPaint}
          />
        </Source>
        {currentMarker && <CustomMarker marker={currentMarker} />}
        {currObjs.length > 0 && (currObjs[0] as CyberSecurityFeature) && (
          <CustomPopup
            latitude={typedCurrObjects[0]?.geometry.coordinates[1]}
            longitude={typedCurrObjects[0]?.geometry.coordinates[0]}
            itemsCount={currObjs.length}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
          >
            {currObjs.map((currObj: GeoJSON.Feature, index: number) =>
              renderPopupItem(currObj as CyberSecurityFeature, index)
            )}
          </CustomPopup>
        )}
      </ReactMapGL>
    </MapWrapper>
  );
};

export default CybersecurityMap;
